import { ChatMessage, ChatSession, SocketChatSession } from '../types';

const messageCreatedTime = (message: ChatMessage): number => {
  const created = message.created;
  if (created instanceof Date) {
    return created.getTime();
  }
  return new Date(created).getTime();
};

/**
 * Rebuild session message map from socket history without wiping cached
 * messages when the server omits them (empty/missing arrays), and without
 * dropping optimistic local messages not yet present in the history payload.
 */
export const mergeSessionMessagesFromHistory = (
  prev: Record<string, ChatMessage[]>,
  history: Array<ChatSession | SocketChatSession>
): Record<string, ChatMessage[]> => {
  const next: Record<string, ChatMessage[]> = {};

  history.forEach(session => {
    const incoming = session.messages ?? [];
    const existing = prev[session.id] ?? [];

    if (incoming.length === 0) {
      if (existing.length > 0) {
        next[session.id] = existing;
      } else {
        next[session.id] = [];
      }
      return;
    }

    const byId = new Map<string, ChatMessage>();
    incoming.forEach(message => {
      if (message?.id) {
        byId.set(message.id, message);
      }
    });
    existing.forEach(message => {
      if (message?.id && !byId.has(message.id)) {
        byId.set(message.id, message);
      }
    });

    next[session.id] = Array.from(byId.values()).sort(
      (a, b) => messageCreatedTime(a) - messageCreatedTime(b)
    );
  });

  return next;
};

export const appendSessionMessageIfNew = (
  prev: Record<string, ChatMessage[]>,
  sessionId: string,
  message: ChatMessage
): Record<string, ChatMessage[]> => {
  const existing = prev[sessionId] ?? [];
  if (message.id && existing.some(item => item.id === message.id)) {
    return prev;
  }

  return {
    ...prev,
    [sessionId]: [...existing, message]
  };
};
